import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { DBO_V } from '@op/utils/format';
import { echartsContent } from '@op/utils';
import { formatChartParamsDate, listTimeTitleFunc } from '@op/components/ChartCommon/formatter';
import { getChartPosition, getChartsGradientColor, getXAxisAxisLabel } from '@op/utils/chart';
import { channelLegendRichInfo } from '@op/components/ChannelLabel';
import { dealWithOthersName } from '@op/utils/languageValue';
import { detailPageTips } from '@op/pages/Overview/KeyMetrics/utils';
import { METRIC_OPTIONS } from '../TagAnalysisCard';

interface IBarLineChartProps {
  data: any;
  filter: any;
  height?: number;
  chartType: 'bar' | 'line' | 'stack_line';
  xAxisKey: string;
  translateTags: (key: string) => string;
  formatMessage: (obj: { id: string; } | string) => string;
  eChartsRef?: React.RefObject<any>;
  metric: string;
  extraProps?: Record<string, any>;
  defaultSelected?: any;
}

const BarLineChart: React.FC<IBarLineChartProps> = ({
  data,
  filter,
  height = 350,
  chartType,
  xAxisKey,
  translateTags,
  eChartsRef,
  formatMessage,
  metric,
  extraProps,
  defaultSelected
}) => {
  const options = useMemo(() => {
    const { legendData, xAxis, series: seriesData, isEmpty } = data || {};

    if (isEmpty) {
      return {};
    }

    const { date_type: dateType } = filter || {};
    const series = seriesData?.map((val: any) => {
      const { key, data, color } = val;
      const item: any = {
        name: key,
        type: chartType === 'stack_line' ? 'line' : chartType,
        data,
        itemStyle: {
          color
        }
      };

      if (['bar', 'stack_line'].includes(chartType)) {
        item.stack = 'Total';
        if (chartType === 'stack_line') {
          item.areaStyle = getChartsGradientColor(color, 0, 0.4);
        }
      }

      return item;
    });

    return {
      series,
      legend: {
        data: legendData,
        formatter: (params: any) => {
          return translateTags(params);
        },
        ...(defaultSelected ? { selected: defaultSelected } : {}),
      },
      tooltip: {
        enterable: true,
        confine: false,
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any]; },
        ) => {
          const value = getChartPosition(
            pos,
            params,
            dom,
            rect,
            size,
            50,
            15,
            'bottom',
            3,
          );
          return value;
        },
        formatter: (params: any) => {
          const titleVal = params[0]?.axisValue;
          const leftTitle = xAxisKey === 'time' ? listTimeTitleFunc(titleVal, dateType) : dealWithOthersName(titleVal, xAxisKey === 'language' ? 'language' : 'channel');
          const metricText = METRIC_OPTIONS.find(option => option.value === metric)?.text || 'Value';

          return formatChartParamsDate({
            className: 'dbo-tag-analysis-chart-tooltip',
            title: leftTitle,
            rightTitle: formatMessage(metricText),
            params,
            isSort: true,
            listTitleFunc: (item: any) => {
              return translateTags(item.seriesName);
            },
            dealwithRecords: (records, total) => {
              const list = records.map((item) => {
                if (item.originValue === 0) return item;

                return {
                  ...item,
                  value: `${item.value}(${DBO_V.percentage(
                    item.originValue,
                    total,
                    2,
                  )})`,
                };
              });

              detailPageTips(list, formatMessage, extraProps?.detailPage);

              return list;
            },
          });
        },
      },
      xAxis: {
        axisLabel: {
          rich: {
            label: {
              padding: [0, 0, 0, 2],
            },
            ...channelLegendRichInfo(16, 16),
          },
          formatter: (val: any) => {
            const formattedValue = dealWithOthersName(val, xAxisKey === 'language' ? 'language' : 'channel');
            return xAxisKey === 'platform' ? `{${val}|}{label|${formattedValue}}` : formattedValue;
          },
          ...(xAxisKey === 'time' ? getXAxisAxisLabel(filter) : {}),
          interval: xAxis?.length > 14 ? 'auto' : 0
        },
        data: xAxis || [],
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => echartsContent(value),
          color: '#838697',
          fontSize: 12,
        },
        axisLine: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
      },
      axisPointer: {
        label: {
          padding: [2, 15],
          formatter: ({ value }: any) => {
            const formattedValue = dealWithOthersName(value, xAxisKey === 'language' ? 'language' : 'channel');
            return formattedValue;
          },
        }
      },
      grid: {
        top: 20
      }
    };
  }, [filter, data, chartType, xAxisKey, translateTags]);


  const realChartType = chartType === 'stack_line' ? 'line' : chartType;

  return (
    <EChartCommon
      optionsProps={{ type: realChartType }}
      height={height}
      options={options}
      ref={eChartsRef}
      {...extraProps}
    />
  );
};

export default BarLineChart;
